import type { Book } from "../../shared/models";
import { loadBookFromBuffer, saveBookToUint8Array } from "../../shared/sbkCompat";
import { isTauriRuntime } from "../lib/runtime";

const BACKUP_FOLDER_KEY = "jw_desktop_backup_folder";
const DEFAULT_BACKUP_REL = "Just Write/Backups";

function safeFilename(title: string, bookId: string): string {
  const base = (title || "book").replace(/[^\w\s-]+/g, "").trim().slice(0, 60) || "book";
  const id = bookId.replace(/[^\w-]+/g, "").slice(0, 12);
  return `${base}${id ? `-${id}` : ""}.sbk`;
}

export function readBackupFolderOverride(): string | null {
  if (typeof localStorage === "undefined") return null;
  const value = localStorage.getItem(BACKUP_FOLDER_KEY)?.trim();
  return value || null;
}

export function writeBackupFolderOverride(path: string | null): void {
  if (typeof localStorage === "undefined") return;
  if (!path?.trim()) {
    localStorage.removeItem(BACKUP_FOLDER_KEY);
    return;
  }
  localStorage.setItem(BACKUP_FOLDER_KEY, path.trim());
}

async function tauriPath() {
  return import("@tauri-apps/api/path");
}

async function tauriFs() {
  return import("@tauri-apps/plugin-fs");
}

async function tauriDialog() {
  return import("@tauri-apps/plugin-dialog");
}

export async function defaultBackupFolder(): Promise<string> {
  const { documentDir, join } = await tauriPath();
  return join(await documentDir(), DEFAULT_BACKUP_REL);
}

export async function resolveBackupFolder(): Promise<string> {
  return readBackupFolderOverride() || (await defaultBackupFolder());
}

export async function chooseBackupFolder(): Promise<string | null> {
  if (!isTauriRuntime()) return null;
  const { open } = await tauriDialog();
  const selected = await open({
    directory: true,
    multiple: false,
    title: "Choose backup folder",
    defaultPath: await resolveBackupFolder(),
  });
  if (typeof selected !== "string" || !selected.trim()) return null;
  writeBackupFolderOverride(selected.trim());
  return selected.trim();
}

export async function pickBackupFile(): Promise<Uint8Array | null> {
  if (!isTauriRuntime()) return null;
  const { open } = await tauriDialog();
  const selected = await open({
    multiple: false,
    title: "Import a Just Write backup",
    filters: [{ name: "Just Write book", extensions: ["sbk"] }],
  });
  if (typeof selected !== "string" || !selected.trim()) return null;
  const { readFile } = await tauriFs();
  return readFile(selected);
}

export type BackupListEntry = {
  id: string;
  title: string;
  author: string;
  updatedAt: string;
  chapterCount: number;
  filePath: string;
};

export async function listBackupBooks(): Promise<BackupListEntry[]> {
  if (!isTauriRuntime()) return [];
  try {
    const folder = await resolveBackupFolder();
    const { readDir, readFile } = await tauriFs();
    const { join } = await tauriPath();
    const entries = await readDir(folder);
    const books: BackupListEntry[] = [];
    for (const entry of entries) {
      if (!entry.name?.toLowerCase().endsWith(".sbk")) continue;
      const filePath = await join(folder, entry.name);
      try {
        const bytes = await readFile(filePath);
        const { book } = await loadBookFromBuffer(bytes);
        const id = typeof book.id === "string" ? book.id.trim() : "";
        if (!id) continue;
        books.push({
          id,
          title: book.title || "Untitled",
          author: book.author || "",
          updatedAt: book.updatedAt || new Date().toISOString(),
          chapterCount: Array.isArray(book.chapters) ? book.chapters.length : 0,
          filePath,
        });
      } catch {
        // skip unreadable backups
      }
    }
    return books;
  } catch {
    return [];
  }
}

export async function writeDesktopBackup(book: Book): Promise<void> {
  if (!isTauriRuntime()) return;
  const id = typeof book.id === "string" ? book.id.trim() : "";
  if (!id) return;
  try {
    const folder = await resolveBackupFolder();
    const { mkdir, writeFile } = await tauriFs();
    const { join } = await tauriPath();
    await mkdir(folder, { recursive: true });
    const { bytes } = await saveBookToUint8Array(book);
    await writeFile(await join(folder, safeFilename(book.title || "Untitled", id)), bytes);
  } catch {
    // backup is best-effort
  }
}

export async function loadBookFromBackupBytes(bytes: Uint8Array | ArrayBuffer): Promise<Book> {
  const { book } = await loadBookFromBuffer(bytes);
  return book;
}
